import {
  dehydrate,
  hydrate,
  type DehydratedState,
  type QueryClient,
} from '@tanstack/react-query';

import { readOfflineValue, writeOfflineValue } from './offlineStore';

/**
 * Keeps the app's query results on the device so a refresh with no connection
 * shows the page as it was. The service worker (apps/work-permit/public/sw.js)
 * serves the page, its JS/CSS and the session; this puts the page's data back.
 *
 * Online, the only change is a save a moment after queries succeed. On an
 * offline copy (a page the worker served from its saved copies),
 * QueryClientProvider waits for {@link loadOfflineCopyQueries} and starts the
 * client with {@link holdOfflineCopy}, so nothing refetches the restored data
 * into an error; {@link startOfflineQueryCache} releases the hold and refetches
 * once the network answers.
 */

const SAVED_QUERIES_KEY = 'react-query';

/** Batches the saves of a page's queries that succeed close together. */
export const SAVE_DELAY_MS = 2000;

/** Older saves are not restored: a day-old list is no longer "the page as it was". */
export const MAX_SAVED_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * How long an offline copy waits for the saved data before starting without
 * it. The page cannot render until the read settles, and an IndexedDB open can
 * wait indefinitely, e.g. behind a delete another tab's connection blocks.
 */
export const SAVED_QUERIES_READ_TIMEOUT_MS = 3000;

/** How often an offline copy checks whether the network is back. */
export const RECONNECT_PROBE_INTERVAL_MS = 5000;

/** Answered by the app itself and never intercepted by the service worker. */
const REACHABILITY_PROBE_URL = '/manifest.webmanifest';

/** Added to a page's <head> by public/sw.js when it serves a saved copy. */
const OFFLINE_COPY_SELECTOR = 'meta[name="insafe-offline-copy"]';

type SavedQueries = {
  appVersion: string | undefined;
  savedAt: number;
  state: DehydratedState;
};

const appVersion = process.env.NEXT_PUBLIC_APP_VERSION;

let savingStopped = false;
let offlineCopyQueries: Promise<DehydratedState | undefined> | undefined;
let networkBack = false;
const networkBackListeners = new Set<() => void>();

/**
 * Whether this page is a saved copy the service worker served because the
 * network failed. Not `navigator.onLine`: that reads online on a network that
 * answers nothing, and wrongly offline in some headless browsers. Always false
 * on the server.
 */
export function isOfflineCopy(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.querySelector(OFFLINE_COPY_SELECTOR) !== null
  );
}

/**
 * Whether the page is an offline copy still waiting for the network. The
 * network banner reads this, since `navigator.onLine` can say online here.
 */
export function isWaitingForNetwork(): boolean {
  return isOfflineCopy() && !networkBack;
}

/** For `useSyncExternalStore` over {@link isWaitingForNetwork}. */
export function subscribeToWaitingForNetwork(listener: () => void): () => void {
  networkBackListeners.add(listener);
  return () => {
    networkBackListeners.delete(listener);
  };
}

async function readSavedQueries(): Promise<DehydratedState | undefined> {
  const saved = await readOfflineValue<SavedQueries>(SAVED_QUERIES_KEY);
  return saved &&
    saved.appVersion === appVersion &&
    Date.now() - saved.savedAt <= MAX_SAVED_AGE_MS
    ? saved.state
    : undefined;
}

/**
 * The last saved queries, if fresh enough and from this app version. Never
 * rejects, and settles within {@link SAVED_QUERIES_READ_TIMEOUT_MS}: the page
 * renders only once it does, so a stuck read must not hold it on a spinner.
 * Read once per page and shared, so a render that suspends on it gets the same
 * promise back when it retries.
 */
export function loadOfflineCopyQueries(): Promise<DehydratedState | undefined> {
  offlineCopyQueries ??= Promise.race([
    readSavedQueries().catch(() => undefined),
    new Promise<undefined>((resolve) => {
      setTimeout(() => resolve(undefined), SAVED_QUERIES_READ_TIMEOUT_MS);
    }),
  ]);
  return offlineCopyQueries;
}

/**
 * Applied over every query's own options while the page is an offline copy:
 * restored data counts as fresh and nothing polls or refetches on its own, so
 * no query fetches, fails, and replaces its data with an error. Queries with
 * nothing saved still fetch on mount, and fail as they would.
 */
const OFFLINE_COPY_HOLD = {
  staleTime: Infinity,
  refetchInterval: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * Puts the saved queries in the client and holds them there until
 * {@link startOfflineQueryCache} sees the network back. Call it while creating
 * the client, before any component renders.
 *
 * The hold sits in `defaultQueryOptions`, where React Query merges a query's
 * options over the client defaults, so it wins over the generated hooks, which
 * pass `staleTime: options?.staleTime` and so on explicitly: as `undefined`,
 * which a client default cannot override.
 */
export function holdOfflineCopy(
  client: QueryClient,
  saved: DehydratedState | undefined,
): void {
  if (saved) {
    hydrate(client, saved);
  }
  const mergeOptions = client.defaultQueryOptions.bind(client);
  client.defaultQueryOptions = ((options) => ({
    ...mergeOptions(options),
    ...OFFLINE_COPY_HOLD,
  })) as QueryClient['defaultQueryOptions'];
}

function releaseOfflineCopy(client: QueryClient): void {
  // Drops the instance override, back to QueryClient.prototype's.
  delete (client as Partial<Pick<QueryClient, 'defaultQueryOptions'>>)
    .defaultQueryOptions;
  networkBack = true;
  networkBackListeners.forEach((listener) => listener());
  void client.refetchQueries({ type: 'active' });
}

async function isNetworkReachable(): Promise<boolean> {
  try {
    await fetch(REACHABILITY_PROBE_URL, { method: 'HEAD', cache: 'no-store' });
    return true;
  } catch {
    return false;
  }
}

/** Probes until the network answers, then calls `onBack` once. */
function watchForNetwork(onBack: () => void): () => void {
  let stopped = false;

  const stop = (): void => {
    stopped = true;
    clearInterval(interval);
    globalThis.removeEventListener('online', probe);
  };

  async function probe(): Promise<void> {
    if (stopped) {
      return;
    }
    const reachable = await isNetworkReachable();
    if (!reachable || stopped) {
      return;
    }
    stop();
    onBack();
  }

  const interval = setInterval(probe, RECONNECT_PROBE_INTERVAL_MS);
  globalThis.addEventListener('online', probe);

  return stop;
}

/**
 * Saves the client's successful queries shortly after fetches succeed, and on
 * an offline copy refetches everything once the network is back. Returns the
 * cleanup.
 *
 * Saves are skipped while no service worker controls the page: without one an
 * offline refresh cannot load the page, so there is nothing to restore into.
 */
export function startOfflineQueryCache(client: QueryClient): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  savingStopped = false;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;

  const save = (): void => {
    saveTimer = undefined;
    if (savingStopped || !navigator.serviceWorker?.controller) {
      return;
    }
    const saved: SavedQueries = {
      appVersion,
      savedAt: Date.now(),
      state: dehydrate(client),
    };
    // Data that cannot be cloned into IndexedDB just isn't kept offline.
    writeOfflineValue(SAVED_QUERIES_KEY, saved).catch(() => undefined);
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (
      event.type === 'updated' &&
      event.action.type === 'success' &&
      saveTimer === undefined
    ) {
      saveTimer = setTimeout(save, SAVE_DELAY_MS);
    }
  });

  const stopWatching = isOfflineCopy()
    ? watchForNetwork(() => releaseOfflineCopy(client))
    : () => undefined;

  return () => {
    unsubscribe();
    clearTimeout(saveTimer);
    stopWatching();
  };
}

/**
 * Stops further saves for this page, so a save already scheduled cannot write
 * the user's data back after sign-out deleted it.
 */
export function stopSavingQueries(): void {
  savingStopped = true;
}
